// "Sign-in methods" on the Account page: the Google / Apple / Facebook sign-in methods linked to
// this account (GET /api/v1/me/social-accounts), with Link (provider SDK, same as sign-in) and
// Unlink (confirm dialog, DELETE) actions. Messages come from ../auth/socialAccounts.js.
import {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Modal} from 'react-bootstrap';
import config from '../config';
import * as AccountApi from '../api/AccountApi';
import {PROVIDER_LABELS, providerErrorMessage} from '../auth/social';
import {
  LOAD_ERROR,
  isNotLinked,
  linkErrorMessage,
  socialRows,
  sortLinked,
  unlinkErrorMessage,
  withLinked,
  withoutLinked,
} from '../auth/socialAccounts';
import {SocialAdapter} from './auth/SsoButtons';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';

const STATUS_ID = 'social-status';
const ERROR_ID = 'social-error';

const focusById = (id) => {
  const el = document.getElementById(id);
  if (el) el.focus();
};

export default function SocialAccountsSection() {
  // null while the list loads (or after a failed load, see loadFailed).
  const [linked, setLinked] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Provider with a request in flight (links and unlinks are one at a time).
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [unlinkProvider, setUnlinkProvider] = useState(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  // Where focus goes once the unlink dialog has closed (its trigger may be gone by then).
  const returnFocusTo = useRef(null);
  const dialogBody = useRef(null);
  const mounted = useRef(false);
  const keys = useIdempotencyKeys();

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadLinked = useCallback(() => {
    setLoadFailed(false);
    AccountApi.listSocialAccounts()
      .then((list) => {
        if (mounted.current) setLinked(sortLinked(list));
      })
      .catch(() => {
        if (mounted.current) setLoadFailed(true);
      });
  }, []);

  useEffect(() => {
    loadLinked();
  }, [loadLinked]);

  const showStatus = (message) => {
    setError('');
    setStatus(message);
    focusById(STATUS_ID);
  };

  const showError = (message) => {
    setStatus('');
    setError(message);
    focusById(ERROR_ID);
  };

  /** Provider SDK token -> POST /api/v1/me/social-accounts. */
  const onToken = (provider, {token}) => {
    setBusy(provider);
    keys
      .run(`link:${provider}`, {provider, token}, (idempotencyKey) =>
        AccountApi.linkSocialAccount(provider, token, {idempotencyKey}),
      )
      .then((res) => {
        if (!mounted.current) return;
        setLinked((prev) => withLinked(prev || [], {provider, email: (res && res.email) || null}));
        showStatus(`${PROVIDER_LABELS[provider]} sign-in linked.`);
      })
      .catch((err) => {
        if (mounted.current) showError(linkErrorMessage(provider, err));
      })
      .finally(() => {
        if (mounted.current) setBusy(null);
      });
  };

  /** The provider popup failed or was closed; a cancelled popup is not an error. */
  const onProviderError = (provider, kind) => {
    const message = providerErrorMessage(provider, kind);
    if (message) showError(message);
  };

  const openUnlink = (provider) => {
    setError('');
    setStatus('');
    setUnlinkProvider(provider);
    setUnlinkOpen(true);
  };

  const closeUnlink = () => {
    if (busy !== null) return;
    returnFocusTo.current = `social-unlink-${unlinkProvider}`;
    setUnlinkOpen(false);
  };

  const restoreFocus = () => {
    const id = returnFocusTo.current;
    returnFocusTo.current = null;
    if (id) focusById(id);
  };

  /**
   * Focus trap for the dialog's buttons: Tab from the last one wraps to the first (and Shift+Tab
   * back). Without it the browser moves focus out of the dialog, which is off the page's DOM order.
   */
  const keepTabInDialog = (event) => {
    if (event.key !== 'Tab' || !dialogBody.current) return;
    const buttons = Array.from(dialogBody.current.querySelectorAll('button:not([disabled])'));
    if (buttons.length === 0) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !buttons.includes(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !buttons.includes(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  /** DELETE /api/v1/me/social-accounts/{provider}, after the user confirmed in the dialog. */
  const confirmUnlink = () => {
    const provider = unlinkProvider;
    setBusy(provider);
    AccountApi.unlinkSocialAccount(provider)
      .then(() => {
        if (!mounted.current) return;
        setLinked((prev) => withoutLinked(prev || [], provider));
        setError('');
        setStatus(`${PROVIDER_LABELS[provider]} sign-in unlinked.`);
        returnFocusTo.current = STATUS_ID;
        setUnlinkOpen(false);
      })
      .catch((err) => {
        if (!mounted.current) return;
        // A 404 means the link was already gone: drop the row so the list matches the server.
        if (isNotLinked(err)) setLinked((prev) => withoutLinked(prev || [], provider));
        setStatus('');
        setError(unlinkErrorMessage(provider, err));
        returnFocusTo.current = ERROR_ID;
        setUnlinkOpen(false);
      })
      .finally(() => {
        if (mounted.current) setBusy(null);
      });
  };

  const unlinkName = unlinkProvider ? PROVIDER_LABELS[unlinkProvider] : '';
  const rows = linked === null ? [] : socialRows(linked, config);

  return (
    <div className="profile-col-card">
      <ul className="list-style-none">
        {rows.map((row) => (
          <li key={row.provider}>
            <div className="profile-item" data-testid={`social-row-${row.provider}`}>
              <div className="profile-name">{row.label}</div>
              <div
                className="profile-info"
                style={{display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center'}}
              >
                <span>{row.linked ? (row.email ? `Linked as ${row.email}` : 'Linked') : 'Not linked'}</span>
                {row.linked ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    aria-label={`Unlink ${row.label} sign-in`}
                    data-testid={`social-unlink-${row.provider}`}
                    id={`social-unlink-${row.provider}`}
                    disabled={busy !== null}
                    onClick={() => openUnlink(row.provider)}
                  >
                    Unlink
                  </button>
                ) : (
                  <SocialAdapter provider={row.provider} onToken={onToken} onProviderError={onProviderError}>
                    {(start) => (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        aria-label={`Link ${row.label} sign-in`}
                        data-testid={`social-link-${row.provider}`}
                        disabled={busy !== null}
                        onClick={start}
                      >
                        Link
                      </button>
                    )}
                  </SocialAdapter>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {linked === null && !loadFailed && <p className="mb-0">Loading sign-in methods...</p>}
      {linked === null && loadFailed && (
        <p className="text-danger mb-0">
          {LOAD_ERROR}{' '}
          <button type="button" className="btn btn-link p-0" onClick={loadLinked}>
            Reload sign-in methods
          </button>
        </p>
      )}
      {linked !== null && rows.length === 0 && (
        <p className="mb-0">No social sign-in methods are set up for this site.</p>
      )}
      {config.authentikSettingsUrl && (
        <p className="mb-0 mt-2" style={{fontSize: '14px'}}>
          <a href={config.authentikSettingsUrl} rel="noreferrer">
            Manage sign-in in Authentik
          </a>
        </p>
      )}
      <div id={STATUS_ID} data-testid="social-status" aria-live="polite" tabIndex={-1} className="mt-2">
        {status}
      </div>
      <div
        id={ERROR_ID}
        data-testid="social-error"
        aria-live="assertive"
        tabIndex={-1}
        className="text-danger mt-2"
      >
        {error}
      </div>
      <Modal
        show={unlinkOpen}
        onHide={closeUnlink}
        onExited={restoreFocus}
        restoreFocus={false}
        centered
        aria-labelledby="social-unlink-title"
      >
        <div ref={dialogBody} onKeyDown={keepTabInDialog}>
          <Modal.Header>
            <Modal.Title id="social-unlink-title">Unlink {unlinkName} sign-in?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-0">You can link {unlinkName} sign-in again later.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeUnlink} disabled={busy !== null}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmUnlink} disabled={busy !== null}>
              Unlink
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
}
